import { Navigate, useOutletContext } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import {
  FeatureFlagAvailabilitiesDocument,
  SurveyAnswerPageEmployeeContractVersionsDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { dateToString } from 'modules/core/lib/dates'

import { isManager } from 'modules/employees/lib/isManager'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import SurveyAnswer from 'modules/surveys/components/Answer/SurveyAnswer'
import SurveyShowHeader from 'modules/surveys/components/Show/SurveyShowHeader'
import SurveyShowPageTabs from 'modules/surveys/components/Show/SurveyShowPageTabs'
import { useQuestionGroup } from 'modules/surveys/hooks/useQuestionGroup'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsTeamLead } from 'modules/teams/store/userIsTeamLead'

const RedirectToResponses = () => {
  const { navigation } = useNavigation()
  const { id } = useStrictParams<'id'>()
  const to = navigation.processes.surveys.id.responses.fullPath({ id })

  return <Navigate to={to} />
}

const AnswerPage = () => {
  const {
    employee,
    company,
    access: { id: userId },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const { survey, canEditSurvey } = useSurveyShowContext()
  const { group } = useOutletContext<{ form: SurveyForm; group?: QuestionGroup }>()
  const id = group?.attributes.id

  // If the survey is loaded but the current user has no question group of
  // their own (e.g. a manager viewing reportees' responses who isn't a
  // respondent themselves), redirect to the Responses page. Without this,
  // useQuestionGroup is called with an undefined id and the page renders
  // blank. Access enforcement still happens on the Responses page itself.
  const shouldRedirectToResponses = !!survey && !group

  const { loading, questionGroup, refresh } = useQuestionGroup(String(id))

  const { data: featuresData, loading: loadingFeatures } = useQuery(
    FeatureFlagAvailabilitiesDocument,
    {
      variables: { companyId: company.id, featureNames: ['surveys_using_forms'] },
    }
  )

  if (!employee) {
    throw new Error('Employee is required')
  }

  const { data: contractVersionsData, loading: loadingContractVersions } = useQuery(
    SurveyAnswerPageEmployeeContractVersionsDocument,
    {
      variables: {
        employeeId: employee.id,
        activeOn: dateToString(moment(), 'backend'),
      },
    }
  )

  const { has_surveys_using_forms: smartSurveysEnabled } = checkFeatures(
    ['surveys_using_forms'],
    featuresData?.features?.featuresConnection.nodes ?? []
  )

  const surveySource = survey?.attributes.source
  const isCustomSurvey = !!survey && (!surveySource || surveySource === 'custom_survey')

  if (smartSurveysEnabled && isCustomSurvey) {
    return <Navigate replace to={navigation.inbox.todo.fullPath()} />
  }

  if (shouldRedirectToResponses) return <RedirectToResponses />

  const jobTitle =
    contractVersionsData?.contracts?.contractVersionsConnection?.nodes[0]?.jobTitle || null

  if (!survey || !questionGroup || loadingFeatures) {
    return null
  }

  const isQuestionGroupRespondent = questionGroup
    ? questionGroup.attributes.respondent_id === userId
    : false

  const canSeeInfo =
    survey.attributes.visible_to_managers &&
    (isManager(employee) || userIsTeamLead(teams, memberships, employee.id))

  if (!isQuestionGroupRespondent && canSeeInfo) return <RedirectToResponses />

  return (
    <ResponsivePage.Body
      showHeader={false}
      loading={loading || loadingContractVersions}
      error={null}
      authorized={isQuestionGroupRespondent}
    >
      <Box gap="s24">
        <SurveyShowHeader survey={survey} canEditSurvey={canEditSurvey} />
        <SurveyShowPageTabs form={survey} canEditSurvey={canEditSurvey} />
      </Box>
      <SurveyAnswer
        loading={loading}
        questionGroup={questionGroup}
        onDraftSaved={refresh}
        form={survey}
        canEditSurvey={canEditSurvey}
        jobTitle={jobTitle}
      />
    </ResponsivePage.Body>
  )
}

export default AnswerPage
